// Adding a company — from a phone number to a ready thread.
//
// TWO numbers, two different jobs, never conflated:
// * the COMPANY's number — what the user typed or scanned; used only to resolve
//   the directory entry. The server address comes from the directory, never
//   from anywhere else.
// * the USER'S OWN number — where the verification SMS goes, and the join key
//   the company's server files the conversation under. Conflating the two was
//   the reference client's dev-mode-blindness bug: with no real SMS provider
//   the buyer's number was never exercised, so nobody missed the second field.
//
// The order of steps is not arbitrary: key registration has to PRECEDE asking
// for the code. Registration costs nothing, so it can be rate-limited as
// aggressively as you like, and the only step that costs money — the SMS —
// stays behind a free gate.

import { keyFor } from '$lib/crypto/device-keys';
import { saveDistributorName, saveFromEntry, saveSession } from '$lib/db/contacts';
import { SETTING_USER_PHONE, setSetting } from '$lib/db/settings';
import { lookupFollowingMoves } from '$lib/directory/directory-repo';

import { completeVerification, registerDevice, startVerification } from './distributor-api';
import { asClientError, type ErrorCode } from './errors';
import type { DirectoryEntry } from './types';

export type AddContactStage =
  | 'entering_number'
  | 'looking_up'
  | 'registering_device'
  | 'awaiting_code'
  | 'verifying_code'
  | 'done';

export interface AddContactState {
  stage: AddContactStage;
  /** The company's number — the directory identity of the contact. */
  phone: string | undefined;
  /** The user's own number — where the code was sent. */
  ownPhone: string | undefined;
  entry: DirectoryEntry | undefined;
  deviceId: string | undefined;
  error: ErrorCode | undefined;
  quarantineUntilMs: number | undefined;
}

const initial: AddContactState = {
  stage: 'entering_number',
  phone: undefined,
  ownPhone: undefined,
  entry: undefined,
  deviceId: undefined,
  error: undefined,
  quarantineUntilMs: undefined,
};

export const addContactInitialState = (): AddContactState => ({ ...initial });

/** The number has to be in international format. The client-side check exists only
 *  to report the problem immediately — the server decides regardless. */
export function isValidNumber(phone: string): boolean {
  return /^\+[1-9][0-9]{6,14}$/.test(normalizeNumber(phone));
}

export function normalizeNumber(phone: string): string {
  return phone.replace(/[\s\-()]/g, '');
}

/** Address-book number → E.164, or undefined when it cannot be normalized with
 *  confidence. `00` becomes `+`; anything without a country code is dropped
 *  rather than guessed at — guessing would silently compare the wrong number. */
export function toE164(raw: string): string | undefined {
  let digits = raw.replace(/[^0-9+]/g, '');
  if (digits.startsWith('00')) {
    digits = `+${digits.slice(2)}`;
  }
  return /^\+[1-9][0-9]{6,14}$/.test(digits) ? digits : undefined;
}

function browserModel(): string {
  const ua = navigator.userAgent;
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/edg\//i.test(ua)) return 'Edge';
  if (/chrome|chromium|crios/i.test(ua)) return 'Chrome';
  if (/safari/i.test(ua)) return 'Safari';
  return 'Browser';
}

/** Steps 1–3: look up the company, register the key, ask for the code — sent to
 *  the USER'S number, never the company's. */
export async function startAddContact(
  rawCompanyNumber: string,
  rawOwnNumber: string,
): Promise<AddContactState> {
  const phone = normalizeNumber(rawCompanyNumber);
  const ownPhone = normalizeNumber(rawOwnNumber);
  if (!isValidNumber(phone) || !isValidNumber(ownPhone) || phone === ownPhone) {
    // The screen explains which field is wrong; this is the belt to its braces.
    return { ...initial, phone, ownPhone, error: 'bad_request' };
  }

  let entry: DirectoryEntry | undefined;
  try {
    // A signed `moved` chain is followed here already, so a user typing the number
    // from an old invoice lands on the successor without any extra step.
    entry = await lookupFollowingMoves(phone);
  } catch (error) {
    return { ...initial, phone, ownPhone, error: asClientError(error).code };
  }

  if (entry === undefined) {
    return { ...initial, phone, ownPhone, error: 'not_found' };
  }
  if (entry.status === 'withdrawn') {
    // A withdrawn entry is no longer reachable for new users. Existing threads
    // stay read-only, but a new conversation cannot be started.
    return { ...initial, phone, ownPhone, entry, error: 'entry_withdrawn' };
  }

  await saveFromEntry(entry);

  try {
    const key = await keyFor(entry.accountId);
    const deviceId = await registerDevice({
      serverUrl: entry.serverUrl,
      key,
      model: browserModel(),
    });
    await startVerification({
      serverUrl: entry.serverUrl,
      accountId: entry.accountId,
      phone: ownPhone,
      deviceId,
    });
    return {
      stage: 'awaiting_code',
      phone,
      ownPhone,
      entry,
      deviceId,
      error: undefined,
      quarantineUntilMs: undefined,
    };
  } catch (error) {
    return { ...initial, phone, ownPhone, entry, error: asClientError(error).code };
  }
}

/** Step 4: the SMS code plus a device-key signature. The server binds the USER'S
 *  number to the device and links the conversation to the customer record it
 *  already keeps under that number. */
export async function confirmAddContact(
  state: AddContactState,
  code: string,
): Promise<AddContactState> {
  const { entry, deviceId, ownPhone } = state;
  if (entry === undefined || deviceId === undefined || ownPhone === undefined) {
    return { ...state, error: 'bad_request' };
  }
  try {
    const session = await completeVerification({
      serverUrl: entry.serverUrl,
      accountId: entry.accountId,
      phone: ownPhone,
      deviceId,
      code,
    });
    await saveSession({
      accountId: entry.accountId,
      token: session.token,
      expiresAtMs: session.expiresAtMs,
      threadId: session.threadId,
      deviceId,
      quarantineUntilMs: session.quarantineUntilMs,
    });
    // The trading name is not in the directory; this is the first moment it can be
    // known, so the conversation list stops showing a bare number from here on.
    await saveDistributorName(entry.accountId, session.distributorName);
    // Remembered locally so the next add-company prefills it — the user has one
    // number and many companies.
    await setSetting(SETTING_USER_PHONE, ownPhone);
    return {
      ...state,
      stage: 'done',
      error: undefined,
      quarantineUntilMs: session.quarantineUntilMs,
    };
  } catch (error) {
    return { ...state, error: asClientError(error).code };
  }
}
